import format from 'date-fns/format';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useApiClient } from '@eluve/api-client-provider';
import { useToast } from '@eluve/components';
import { useNamedLogger } from '@eluve/logger';
import { useAnalytics } from '@eluve/posthog-react';
import {
  useAssignedTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import { useConfigStore } from '@eluve/smart-blocks';

export const useStartAnonymousSession = () => {
  const logger = useNamedLogger('HomePage');
  const tenantId = useAssignedTenantIdFromParams();
  const userId = useUserIdFromSession();
  const { config } = useConfigStore(tenantId, userId);
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const apiClient = useApiClient();
  const toast = useToast();
  const [isPending, setIsPending] = useState(false);

  const startSession = useCallback(async () => {
    if (isPending) return;
    setIsPending(true);
    const startDate = new Date();

    try {
      const result = await apiClient.appointments.createAppointment({
        params: { tenantId },
        body: {
          startDate: startDate.toISOString(),
          name: `New Session ${format(startDate, 'yyyy-MM-dd HH:mm')}`,
          locationId: config.activeLocationId ?? undefined,
        },
      });

      if (result.status === 201) {
        const appointmentId = result.body.id;
        analytics.capture('created_new_session', {
          tenantId,
          appointmentId,
        });
        navigate(`/organizations/${tenantId}/appointments/${appointmentId}`);
        setIsPending(false);
        return;
      }

      logger.warn('Error creating new appointment', {
        status: result.status,
      });
      toast.error('Failed to create appointment. Please try again.');
      setIsPending(false);
    } catch (error) {
      logger.warn(
        'Failed to create appointment. Please check your internet connection and try again.',
        { error },
      );
      toast.error(
        'Failed to create appointment. Please check your internet connection and try again.',
      );
      setIsPending(false);
    }
  }, [
    analytics,
    apiClient,
    config.activeLocationId,
    isPending,
    logger,
    navigate,
    tenantId,
    toast,
  ]);

  return { startSession, isPending };
};
